import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { CronApiConfig } from './types';

export class BaseClient {
  protected client: AxiosInstance;

  constructor(config: CronApiConfig) {
    this.client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 10000,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
    });

    // Add basic auth if provided
    if (config.auth) {
      this.client.defaults.auth = {
        username: config.auth.username,
        password: config.auth.password,
      };
    }

    // Add response interceptor for consistent error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        if (error.response) {
          // Server responded with error status
          const message = error.response.data?.error || error.response.statusText;
          throw new Error(`API Error (${error.response.status}): ${message}`);
        } else if (error.request) {
          // Request was made but no response received
          throw new Error('Network Error: No response received from server');
        } else {
          // Something else happened
          throw new Error(`Request Error: ${error.message}`);
        }
      },
    );
  }

  protected async _get<T>(url: string): Promise<T> {
    const response = await this.client.get<T>(url);
    return response.data;
  }

  protected async _post<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  protected async _put<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  protected async _delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  /**
   * Health check endpoint
   */
  public async ping(): Promise<{ message: string }> {
    return this._get<{ message: string }>('/api/ping');
  }
}
